import {
  ActionReducerMapBuilder,
  createAction,
  createAsyncThunk,
} from '@reduxjs/toolkit';
import { classToPlain } from 'class-transformer';

import { AppError } from '@vk-hr-tek/core/error';
import { showNotification, showError } from '@vk-hr-tek/core/notifications';

import { BellNotification, BellService } from '@app/gen/bell';

import { ThunkExtra } from '../../../app/store';
import {
  pageNotificationsAdapter,
  BellNotificationsState,
  BellNotificationsWithRootState,
} from '../bellNotifications.state';
import {
  GetBellNotificationsDto,
  MarkReadAllDto,
  MarkReadDto,
} from '../../dto';

export const getListNotifications = createAsyncThunk<
  BellNotification[],
  GetBellNotificationsDto | undefined,
  ThunkExtra<BellNotificationsWithRootState>
>(
  'bellNotifications/getListNotifications',
  async (dto, { rejectWithValue, extra: { inject } }) => {
    try {
      const service = inject(BellService);

      const result = await service.getUserBell({
        ...(dto && {
          query: {
            limit: dto.limit,
            last_id: dto.lastId,
            is_read: dto.isRead,
          },
        }),
      });

      return result.bell_notifications;
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const getAdditionalListNotifications = createAsyncThunk<
  BellNotification[],
  GetBellNotificationsDto | undefined,
  ThunkExtra<BellNotificationsWithRootState>
>(
  'bellNotifications/getAdditionalListNotifications',
  async (dto, { rejectWithValue, extra: { inject } }) => {
    try {
      const service = inject(BellService);

      const result = await service.getUserBell({
        ...(dto && {
          query: {
            limit: dto.limit,
            last_id: dto.lastId,
            is_read: dto.isRead,
          },
        }),
      });

      return result.bell_notifications;
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const getUnreadNotificationsCounter = createAsyncThunk<
  number,
  undefined,
  ThunkExtra<BellNotificationsWithRootState>
>(
  'bellNotifications/getUnreadNotificationsCounter',
  async (_, { rejectWithValue, extra: { inject } }) => {
    try {
      const service = inject(BellService);

      const result = await service.getUserBellCounter({
        query: { is_read: false },
      });

      return result.bell_notification_counter;
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

const markReadSync = createAction<string>('bellNotifications/markReadSync');

export const markRead = createAsyncThunk<
  void,
  MarkReadDto,
  ThunkExtra<BellNotificationsWithRootState>
>(
  'bellNotifications/markRead',
  async (dto, { rejectWithValue, dispatch, extra: { inject } }) => {
    try {
      const service = inject(BellService);

      dispatch(markReadSync(dto.id));

      await service.bellNotificationMarkRead({
        body: { notification_ids: [dto.id] },
      });
    } catch (err) {
      dispatch(
        showError(
          'Не удалось отметить уведомление прочитанным\nПопробуйте еще раз позже',
        ),
      );
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const markReadAll = createAsyncThunk<
  void,
  MarkReadAllDto,
  ThunkExtra<BellNotificationsWithRootState>
>(
  'bellNotifications/markReadAll',
  async (dto, { rejectWithValue, dispatch, getState, extra: { inject } }) => {
    const { unreadNotificationsCount } = getState().bellNotifications.page;

    try {
      const service = inject(BellService);

      const response = await service.bellNotificationMarkReadAll();

      dispatch(
        showNotification(
          `Все уведомления отмечены прочитанными (${response.marked_count})`,
        ),
      );
    } catch (err) {
      dispatch(
        showError(
          `Не удалось отметить все уведомления прочитанными (${unreadNotificationsCount})\nПопробуйте еще раз позже`,
        ),
      );
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const listNotificationActionsReducers = (
  builder: ActionReducerMapBuilder<BellNotificationsState>,
) => {
  builder.addCase(getListNotifications.pending, (state) => {
    state.page.status = 'loading';
    state.page.error = null;
  });
  builder.addCase(
    getListNotifications.fulfilled,
    (state, { payload, meta }) => {
      state.page.status = 'complete';
      state.page.error = null;
      state.page.currentLastNotificationId = payload[payload.length - 1]?.id;

      if (meta.arg?.limit)
        state.page.hasMoreNotifications = payload.length >= meta.arg.limit;

      pageNotificationsAdapter.setAll(state.page, payload);
    },
  );
  builder.addCase(
    getListNotifications.rejected,
    (state, { payload, error }) => {
      state.page.status = 'failed';
      pageNotificationsAdapter.removeAll(state.page);
      state.page.error =
        /* istanbul ignore next */
        payload ||
        ({
          info: (error && error.message) || 'Unknown error',
          status: 500,
          source: 'client',
          title: 'Internal client error',
        } as AppError);
    },
  );
  builder.addCase(
    getAdditionalListNotifications.fulfilled,
    (state, { payload, meta }) => {
      state.page.currentLastNotificationId = payload[payload.length - 1]?.id;

      if (meta.arg?.limit)
        state.page.hasMoreNotifications = payload.length >= meta.arg.limit;

      pageNotificationsAdapter.upsertMany(state.page, payload);
    },
  );
  builder.addCase(getUnreadNotificationsCounter.pending, (state) => {
    state.page.isGetUnreadNotificationsCounterPending = true;
  });
  builder.addCase(
    getUnreadNotificationsCounter.fulfilled,
    (state, { payload }) => {
      state.page.unreadNotificationsCount = payload;
      state.page.isGetUnreadNotificationsCounterPending = false;
    },
  );
  builder.addCase(getUnreadNotificationsCounter.rejected, (state) => {
    state.page.isGetUnreadNotificationsCounterPending = false;
  });
  builder.addCase(markReadSync, (state, { payload }) => {
    pageNotificationsAdapter.updateOne(state.page, {
      id: payload,
      changes: { read_at: new Date().toISOString() },
    });
  });
  builder.addCase(markRead.fulfilled, (state, { meta }) => {
    if (
      state.page.unreadNotificationsCount &&
      state.page.unreadNotificationsCount > 0
    )
      state.page.unreadNotificationsCount =
        state.page.unreadNotificationsCount - 1;

    if (
      state.bell.unreadNotificationsCount &&
      state.bell.unreadNotificationsCount > 0
    )
      state.bell.unreadNotificationsCount =
        state.bell.unreadNotificationsCount - 1;

    if (meta.arg.removeFromList)
      pageNotificationsAdapter.removeOne(state.page, meta.arg.id);
  });
  builder.addCase(markRead.rejected, (state, { meta }) => {
    pageNotificationsAdapter.updateOne(state.page, {
      id: meta.arg.id,
      changes: { read_at: undefined },
    });
  });
  builder.addCase(markReadAll.pending, (state) => {
    state.page.isMarkReadAllPending = true;
  });
  builder.addCase(markReadAll.fulfilled, (state, { meta }) => {
    if (!!meta.arg.removeFromList)
      pageNotificationsAdapter.removeAll(state.page);
    else {
      const now = new Date().toISOString();

      const updates = state.page.ids.map((id) => ({
        id,
        changes: { read_at: now },
      }));

      pageNotificationsAdapter.updateMany(state.page, updates);
    }

    state.page.unreadNotificationsCount = null;
    state.bell.unreadNotificationsCount = null;

    state.page.isMarkReadAllPending = false;
  });
  builder.addCase(markReadAll.rejected, (state) => {
    state.page.isMarkReadAllPending = false;
  });
};
